import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { validatePassword, PasswordRequirements } from '../utils/passwordValidation';
import ThemeBackground from '../components/Layout/ThemeBackground';

function SetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!token) {
      setError('Invalid setup token');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    const validation = validatePassword(password);
    if (!validation.isValid) {
      setError(validation.errors.join('\n'));
      setLoading(false);
      return;
    }

    try {
      console.log('Setting up password with token:', token?.substring(0, 10) + '...');
      const response = await api.post(`/api/auth/reset-password/${token}`, { password });

      console.log('Password setup response:', response.data);
      
      setSuccess(true);
      setTimeout(() => {
        // If this is a first-time setup, go directly to InitialSetup with auto-login
        if (response.data.requiresInitialSetup) {
          // Store the auth token from password setup
          if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          }
          navigate('/initial-setup', { 
            state: { 
              fromPasswordSetup: true,
              message: 'Password set successfully! Let\'s complete your account setup.' 
            }
          });
        } else {
          navigate('/login', {
            state: {
              message: 'Password set successfully! You can now log in with your new password.'
            }
          });
        }
      }, 2000);
    } catch (error: any) {
      console.error('Error setting password:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error ||
                          (error.response?.status === 400 ? 'Invalid or expired setup link. Please contact your administrator for a new link.' : 'Failed to set password. Please try again or contact support.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <ThemeBackground variant="full">
        <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="glass-card py-8 px-4 sm:px-10">
              <div className="text-green-400 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium theme-text-primary mb-2">Password Set Successfully!</h3>
              <p className="text-sm theme-text-secondary mb-2">Continuing to account setup...</p>
              <p className="text-xs theme-text-muted">Next, you'll set up multi-factor authentication for enhanced security.</p>
            </div>
          </div>
        </div>
      </div>
      </ThemeBackground>
    );
  }

  return (
    <ThemeBackground variant="full">
      <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-white">
              Set Your Password
            </h2>
            <p className="mt-2 text-base text-blue-100">
              Welcome to Standard Practice! Create a secure password for your new account.
            </p>
          </div>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-2xl">
          <div className="glass-card py-8 px-4 sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-blue-100">
                Create Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="theme-input"
                  placeholder="Enter your new password"
                />
              </div>
              <PasswordRequirements password={password} />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-blue-100">
                Confirm Password
              </label>
              <div className="mt-1">
                <input
                  id="confirmPassword"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="theme-input"
                  placeholder="Confirm your new password"
                />
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <div className="text-sm font-medium text-red-800 whitespace-pre-line">{error}</div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  loading ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
              >
                {loading ? 'Setting Password...' : 'Set Password & Continue'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/20" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-gradient-to-br from-indigo-900/80 via-blue-900/80 to-indigo-950/80 text-blue-200 font-medium z-10">Next Steps</span>
              </div>
            </div>
            <div className="mt-4 text-center">
              <p className="text-sm text-blue-200">
                After setting your password, you'll complete your account setup including multi-factor authentication for enhanced security.
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>
    </ThemeBackground>
  );
}

export default SetPassword;
